"use client"

import { useState, useMemo, useRef, useEffect } from "react"
import { Filter, Globe, Layers, X, Search, RotateCcw, Plus, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { useTranslation } from "@/hooks/use-translation"
import { useLocalizedField } from "@/utils/language-utils"
import { useLanguage } from "@/context/language-context"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useCodeValues } from "@/hooks/use-code-values"
import { getSearchColumnPath, buildSearchableFilter, buildFacetedValuesFilter, isDateField } from "@/utils/column-type-helpers"
import { ModernDatePicker } from "@/components/modern-date-picker"
import { useViewManager } from "../view-manager-context"
import type { FilterDto, StageFilterDto } from "@/types/saved-view"

// ─── Filter Value Selector (checkbox list for facet fields) ──────

function FilterValueSelector({ title, options, selectedValues, onChange, disabled = false }: {
  title: string
  options: Array<{ label: string; value: string; count?: number }>
  selectedValues: string[]
  onChange: (values: string[]) => void
  disabled?: boolean
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const { t } = useTranslation()

  const BLANK_VALUE = '__BLANK__'
  const blankOption = { label: t('blank') || '(blank)', value: BLANK_VALUE, count: undefined }
  const filteredOptions = options.filter(opt => {
    if (!searchTerm) return true
    return opt.label.toLowerCase().includes(searchTerm.toLowerCase()) || opt.value.toLowerCase().includes(searchTerm.toLowerCase())
  })
  const shouldIncludeBlank = !searchTerm || 'blank'.includes(searchTerm.toLowerCase())

  const sortedOptions = useMemo(() => {
    const sorted = [...filteredOptions].sort((a, b) => {
      const aSelected = selectedValues.includes(String(a.value))
      const bSelected = selectedValues.includes(String(b.value))
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      return (a.label || '').localeCompare(b.label || '')
    })
    if (shouldIncludeBlank) return [blankOption, ...sorted]
    return sorted
  }, [filteredOptions, shouldIncludeBlank, selectedValues])

  const allOptions = sortedOptions
  const isAllSelected = allOptions.length > 0 && allOptions.every(o => selectedValues.includes(String(o.value)))
  const hasPartial = selectedValues.length > 0 && !isAllSelected

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span className="text-sm font-semibold text-[#0F214B]">{title}</span>
        {selectedValues.length > 0 && (
          <span className="ml-2 text-xs text-blue-600">({selectedValues.length})</span>
        )}
      </div>
      <div className="p-2">
        <div className="relative mb-2">
          <Input
            placeholder={`${t('search') || 'Search'} ${title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-7 text-xs pr-7"
            disabled={disabled}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600" aria-label="Clear search">
              <X size={12} />
            </button>
          )}
        </div>
        <div className="max-h-[200px] overflow-y-auto">
          <div className="flex items-center py-1 px-1 border-b border-gray-100 mb-1">
            <Checkbox
              checked={isAllSelected ? true : hasPartial ? 'indeterminate' : false}
              disabled={disabled}
              onCheckedChange={(checked) => { if (!disabled) onChange(checked ? allOptions.map(o => o.value) : []) }}
            />
            <label className={`ml-2 text-xs font-medium ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>{t('selectAll') || 'Select all'}</label>
          </div>
          {sortedOptions.length === 0 ? (
            <p className="text-xs text-gray-400 py-2 text-center">No options found</p>
          ) : (
            sortedOptions.map(opt => (
              <div key={opt.value} className="flex items-center py-0.5 px-1">
                <Checkbox
                  checked={selectedValues.includes(String(opt.value))}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    if (!disabled) onChange(checked
                      ? [...selectedValues, opt.value]
                      : selectedValues.filter(v => String(v) !== String(opt.value))
                    )
                  }}
                />
                <label className="ml-2 text-xs text-gray-700">
                  {opt.label} {opt.count !== undefined && <span className="text-gray-400">({opt.count})</span>}
                </label>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Search Filter Input (for isSearchable text fields with suggestions) ──

function SearchFilterInput({ title, value, onChange, columnCode, disabled = false, fetchSuggestions }: {
  title: string
  value: string
  onChange: (searchText: string) => void
  columnCode: string
  disabled?: boolean
  fetchSuggestions?: (text: string, columnCode: string) => Promise<any>
}) {
  const { t } = useTranslation()
  const [localValue, setLocalValue] = useState(value)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const latestRequestIdRef = useRef(0)

  // Sync localValue when the external value prop changes (e.g. switching selected filter)
  useEffect(() => {
    setLocalValue(value)
  }, [value, columnCode])

  // Fetch suggestions on input change
  const handleInputChange = (text: string) => {
    setLocalValue(text)
    if (disabled || !fetchSuggestions || text.trim().length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    // Only show suggestions for specific fields (matches grid whitelist)
    const suggestionFields = ['WorkerFullName', 'WorkerEmail', 'RecruiterFullName', 'RecruiterEmail', 'ManagerFullName', 'ManagerEmail', 'PrimaryJobOwnerFullName', 'PrimaryJobOwnerEmail']
    if (!suggestionFields.includes(columnCode)) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current)
    suggestTimeoutRef.current = setTimeout(async () => {
      const requestId = ++latestRequestIdRef.current
      try {
        const result = await fetchSuggestions(text.trim(), columnCode)
        // Only update state if this is still the latest request (prevents stale overwrites)
        if (requestId !== latestRequestIdRef.current) return
        let suggestionsArray: any[] = []
        if (Array.isArray(result)) suggestionsArray = result
        else if (result?.value && Array.isArray(result.value)) suggestionsArray = result.value
        const values = suggestionsArray.map((s: any) => typeof s === 'string' ? s : (s.text || s)).filter(Boolean)
        setSuggestions(values)
        setShowSuggestions(values.length > 0)
      } catch {
        if (requestId !== latestRequestIdRef.current) return
        setSuggestions([])
        setShowSuggestions(false)
      }
    }, 1000)
  }

  const handleApply = () => {
    setShowSuggestions(false)
    onChange(localValue.trim())
  }

  const handleSelectSuggestion = (suggestion: string) => {
    setLocalValue(suggestion)
    setShowSuggestions(false)
    onChange(suggestion)
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <span className="text-sm font-semibold text-[#0F214B]">{title}</span>
        {value && <span className="ml-2 text-xs text-blue-600">("{value}")</span>}
      </div>
      <div className="p-2">
        <div className="relative">
          <div className="flex gap-1">
            <Input
              placeholder={`${t('search') || 'Search'} ${title.toLowerCase()}...`}
              value={localValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleApply() }}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              className="h-8 text-xs flex-1"
              disabled={disabled}
              maxLength={100}
            />
            {!disabled && (
              <Button variant="outline" size="sm" onClick={handleApply} className="h-8 text-xs px-3">
                {t('apply') || 'Apply'}
              </Button>
            )}
            {!disabled && localValue && (
              <Button variant="outline" size="sm" onClick={() => { setLocalValue(''); onChange(''); setSuggestions([]) }} className="h-8 text-xs px-2 text-red-500 hover:bg-red-50">
                <X size={12} />
              </Button>
            )}
          </div>
          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {suggestions.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectSuggestion(s)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b border-gray-50 last:border-b-0"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
        {!disabled && localValue.length >= 100 && (
          <p className="text-[10px] text-red-500 mt-1">{t('searchCharLimit')}</p>
        )}
        {!disabled && localValue.length < 100 && (
          <p className="text-[10px] text-gray-400 mt-1">Type and press Enter or Apply. Suggestions will appear as you type.</p>
        )}
      </div>
    </div>
  )
}

// ─── Date Range Filter (from/to date pickers for date fields) ──

function DateRangeFilterInput({ title, start, end, onChange, disabled = false }: {
  title: string
  start: string | null
  end: string | null
  onChange: (start: string | null, end: string | null) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <span className="text-sm font-semibold text-[#0F214B]">{title}</span>
        {(start || end) && (
          <span className="ml-2 text-xs text-blue-600">
            ({start || '...'} → {end || '...'})
          </span>
        )}
      </div>
      <div className="p-3 space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">{t('from' as any) || 'From'}</label>
          <ModernDatePicker
            value={start}
            onChange={(val) => onChange(val, end)}
            placeholder={t('selectStartDate' as any) || 'Select start date...'}
            disabled={disabled}
            maxDate={end}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">{t('to' as any) || 'To'}</label>
          <ModernDatePicker
            value={end}
            onChange={(val) => onChange(start, val)}
            placeholder={t('selectEndDate' as any) || 'Select end date...'}
            disabled={disabled}
            minDate={start}
          />
        </div>
        {!disabled && (start || end) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange(null, null)}
            className="w-full text-xs text-red-500 hover:bg-red-50 border-red-200"
          >
            <X size={12} className="mr-1" />
            {t('clearFilters' as any) || 'Clear Date Filter'}
          </Button>
        )}
      </div>
    </div>
  )
}

// ─── Parse filter metadata ──────────────────────────────────────

function parseFilterValues(filter: FilterDto | StageFilterDto): { search?: string; regularValues?: string[]; hasBlanks?: boolean; isSelectAll?: boolean; start?: string | null; end?: string | null } | null {
  const raw = (filter as any).values
  if (!raw) return null
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw } catch { return null }
}

/** Get a short preview of filter values for pills, with overflow count */
function getFilterPreview(filter: FilterDto | StageFilterDto): { text: string; overflow: number } {
  const meta = parseFilterValues(filter)
  if (meta?.start || meta?.end) {
    const parts: string[] = []
    if (meta.start) parts.push(meta.start)
    if (meta.end && meta.end !== meta.start) parts.push(meta.end)
    return { text: parts.join(' → '), overflow: 0 }
  }
  if (meta?.search) return { text: `"${meta.search}"`, overflow: 0 }
  if (meta?.isSelectAll) return { text: 'All', overflow: 0 }
  const vals = [...(meta?.regularValues || [])]
  if (meta?.hasBlanks) vals.push('(blank)')
  if (vals.length === 0) return { text: '', overflow: 0 }
  const shown = vals.slice(0, 2).join(', ')
  return { text: shown, overflow: Math.max(0, vals.length - 2) }
}

// ─── Filters Step ───────────────────────────────────────────────

type FilterView = 'overview' | 'global' | 'stage'

export function FiltersStep() {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const { state, dispatch } = useViewManager()
  const getLocalizedField = useLocalizedField()
  const { workOrdersFacets, facets, assignedUsers } = useWorkflow()
  const codeValues = useCodeValues()
  const { fetchSearchSuggestions } = useWorkflowApi()
  const [activeView, setActiveView] = useState<FilterView>('overview')
  const [activeStageForFilters, setActiveStageForFilters] = useState(state.activeEditStage)
  // Selected filter for the right-side detail panel in overview mode
  const [selectedFilter, setSelectedFilter] = useState<{ fieldId: string; stageId?: string; type: 'global' | 'stage' } | null>(null)
  // Field search query for Global/Stage filter tabs (Item 1)
  const [fieldSearchQuery, setFieldSearchQuery] = useState('')
  // Add-filter dropdown state for Overview (Item 2)
  const [addFilterTarget, setAddFilterTarget] = useState<{ type: 'global' | 'stage'; stageId?: string } | null>(null)
  const [addFilterSearch, setAddFilterSearch] = useState('')
  const addFilterRef = useRef<HTMLDivElement>(null)
  // Sort dropdown state for Overview stage cards
  const [sortDropdownStageId, setSortDropdownStageId] = useState<string | null>(null)
  const [sortDropdownSearch, setSortDropdownSearch] = useState('')
  const sortDropdownRef = useRef<HTMLDivElement>(null)

  // Clear field search when switching tabs/stages
  const handleSetActiveView = (view: FilterView) => {
    setActiveView(view)
    setFieldSearchQuery('')
    setSelectedFilter(null) // Item 4: defensive clear
    setAddFilterTarget(null)
  }
  const handleSetActiveStage = (stageId: string) => {
    setActiveStageForFilters(stageId)
    setFieldSearchQuery('')
  }

  // Close add-filter dropdown when clicking outside
  useEffect(() => {
    if (!addFilterTarget && !sortDropdownStageId) return
    const handleClickOutside = (e: MouseEvent) => {
      if (addFilterRef.current && !addFilterRef.current.contains(e.target as Node)) {
        setAddFilterTarget(null)
        setAddFilterSearch('')
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
        setSortDropdownStageId(null)
        setSortDropdownSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [addFilterTarget, sortDropdownStageId])

  if (!state.selectedView) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        {t('vmSelectViewFirst' as any) || 'Select a view from the Views tab to configure filters'}
      </div>
    )
  }

  const stages = state.originalConfig?.stageConfigurations || []
  const fieldsInUse = state.originalConfig?.fieldsInUse || []
  const globalFilterIds = state.originalConfig?.globalFilterIds || []

  // Build field lookup
  const fieldMap = useMemo(() => {
    const map = new Map<string, any>()
    for (const f of fieldsInUse) map.set(f.fieldConfigurationId, f)
    for (const stage of stages) {
      for (const f of (stage.fieldConfigurations || [])) map.set(f.fieldConfigurationId, f)
    }
    return map
  }, [fieldsInUse, stages])

  const getFieldName = (fieldId: string) => {
    const field = fieldMap.get(fieldId)
    return field ? (getLocalizedField(field, 'columnName') || field.columnCode) : fieldId
  }

  const getStageName = (stageId: string) => {
    const stage = stages.find((s: any) => (s.stageId || s.stageCode) === stageId)
    return stage ? (getLocalizedField(stage, 'title') || stage.stageCode) : stageId
  }

  // Check if a field is searchable-only (not facetable)
  const isSearchableOnly = (field: any) => field?.isSearchable && !field?.isFacetable && !field?.isFilterable

  /**
   * Get facet options for a filter field.
   * For global filters: uses `facets` (from the cross-stage global facets search) as primary source.
   * For stage filters: uses `workOrdersFacets` (from per-stage search) as primary source.
   */
  const getFacetOptions = (fieldId: string, isGlobalFilter: boolean = false): Array<{ label: string; value: string; count?: number }> => {
    const field = fieldMap.get(fieldId)
    if (!field) return []
    if (field.codeValueSource && codeValues?.isReady) {
      try {
        const cvList = codeValues.getCodeValues(field.codeValueSource, false)
        if (cvList && Array.isArray(cvList)) {
          return cvList.filter((item: any) => item?.code?.trim() && item?.text?.trim())
            .map((item: any) => ({ value: item.code, label: item.text, count: undefined }))
        }
      } catch { /* fall through */ }
    }
    // Global filters use `facets` (cross-stage), stage filters use `workOrdersFacets` (per-stage)
    const facetSources = isGlobalFilter
      ? [facets, workOrdersFacets].filter(Boolean)
      : [workOrdersFacets, facets].filter(Boolean)
    if (facetSources.length === 0) {
      // Last resort: if the field has customValues, use those
      if (field.customValues && Array.isArray(field.customValues)) {
        return field.customValues
          .filter((v: any) => !v.isDeleted && (v.titleEnglish || v.code))
          .map((v: any) => ({
            value: v.code || v.titleEnglish,
            label: language === 'fr' ? (v.titleFrench || v.titleEnglish || v.code) : (v.titleEnglish || v.code),
            count: undefined,
          }))
      }
      return []
    }
    const facetKey = getSearchColumnPath(field)
    const tryKeys = [facetKey, field.columnCode, ...Object.keys(facetSources[0] || {}).filter(k => k.toLowerCase() === (field.columnCode || '').toLowerCase())]
    for (const source of facetSources) {
      if (!source) continue
    for (const key of tryKeys) {
      const data = source[key]
      if (Array.isArray(data) && data.length > 0) {
        return data.filter((opt: any) => (opt.value ?? opt.Value ?? '') !== '').map((opt: any) => {
          const value = String(opt.value ?? opt.Value ?? '')
          let label = value
          if ((field.columnCode === 'AssignedToUser' || field.columnCode === 'AssignedToUser/Email') && Array.isArray(assignedUsers)) {
            const user = assignedUsers.find((u: any) => u.email?.toLowerCase() === value.toLowerCase())
            if (user?.fullName) label = user.fullName
          } else if (field.columnTypeCode?.toLowerCase() === 'boolean') {
            label = value === 'true' ? (t('yes') || 'Yes') : value === 'false' ? (t('no') || 'No') : value
          } else if (opt.titleEnglish || opt.titleFrench) {
            label = language === 'fr' ? (opt.titleFrench || opt.titleEnglish || value) : (opt.titleEnglish || value)
          } else if (field.codeValueSource && codeValues?.isReady) {
            try { const dt = codeValues.getCodeValueTextByCode(value, field.codeValueSource); if (dt) label = dt } catch {}
          }
          return { value, label, count: opt.count ?? opt.Count }
        })
      }
    }
    }
    // Last resort: customValues on the field itself
    if (field.customValues && Array.isArray(field.customValues)) {
      const filtered = field.customValues.filter((v: any) => !v.isDeleted && (v.titleEnglish || v.code))
      if (filtered.length > 0) {
        return filtered.map((v: any) => ({
          value: v.code || v.titleEnglish,
          label: language === 'fr' ? (v.titleFrench || v.titleEnglish || v.code) : (v.titleEnglish || v.code),
          count: undefined,
        }))
      }
    }
    return []
  }

  // ── Filter helpers ──

  const getGlobalFilterSelections = (fieldId: string): string[] => {
    const filter = state.draftGlobalFilters.find(f => f.fieldConfigurationId === fieldId)
    if (!filter) return []
    const meta = parseFilterValues(filter)
    const selections: string[] = []
    if (meta?.hasBlanks) selections.push('__BLANK__')
    if (meta?.regularValues) selections.push(...meta.regularValues)
    return selections
  }

  const getStageFilterSearch = (stageId: string, fieldId: string): string => {
    const filter = state.draftStageFilters.find(f => f.stageId === stageId && f.fieldConfigurationId === fieldId)
    if (!filter) return ''
    const meta = parseFilterValues(filter)
    return meta?.search || ''
  }

  const getGlobalFilterSearch = (fieldId: string): string => {
    const filter = state.draftGlobalFilters.find(f => f.fieldConfigurationId === fieldId)
    if (!filter) return ''
    const meta = parseFilterValues(filter)
    return meta?.search || ''
  }

  const getStageFilterSelections = (stageId: string, fieldId: string): string[] => {
    const filter = state.draftStageFilters.find(f => f.stageId === stageId && f.fieldConfigurationId === fieldId)
    if (!filter) return []
    const meta = parseFilterValues(filter)
    const selections: string[] = []
    if (meta?.hasBlanks) selections.push('__BLANK__')
    if (meta?.regularValues) selections.push(...meta.regularValues)
    return selections
  }

  // ── Date filter helpers ──

  const getDateFilterValues = (stageId: string, fieldId: string): { start: string | null; end: string | null } => {
    const filter = state.draftStageFilters.find(f => f.stageId === stageId && f.fieldConfigurationId === fieldId)
    if (!filter) return { start: null, end: null }
    const meta = parseFilterValues(filter)
    return { start: meta?.start || null, end: meta?.end || null }
  }

  const getGlobalDateFilterValues = (fieldId: string): { start: string | null; end: string | null } => {
    const filter = state.draftGlobalFilters.find(f => f.fieldConfigurationId === fieldId)
    if (!filter) return { start: null, end: null }
    const meta = parseFilterValues(filter)
    return { start: meta?.start || null, end: meta?.end || null }
  }

  /** Build an OData date-range filter string from start/end date strings */
  const buildDateFilterString = (columnCode: string, start: string | null, end: string | null): string => {
    if (start && !end) {
      const startStr = `${start}T00:00:00Z`
      const nextDay = new Date(Date.UTC(parseInt(start.split('-')[0]), parseInt(start.split('-')[1]) - 1, parseInt(start.split('-')[2]) + 1))
      const nextDayStr = `${nextDay.toISOString().split('T')[0]}T00:00:00Z`
      return `${columnCode} ge ${startStr} and ${columnCode} lt ${nextDayStr}`
    } else if (start && end) {
      const startStr = `${start}T00:00:00Z`
      const nextDay = new Date(Date.UTC(parseInt(end.split('-')[0]), parseInt(end.split('-')[1]) - 1, parseInt(end.split('-')[2]) + 1))
      const nextDayStr = `${nextDay.toISOString().split('T')[0]}T00:00:00Z`
      return `${columnCode} ge ${startStr} and ${columnCode} lt ${nextDayStr}`
    } else if (!start && end) {
      const nextDay = new Date(Date.UTC(parseInt(end.split('-')[0]), parseInt(end.split('-')[1]) - 1, parseInt(end.split('-')[2]) + 1))
      const nextDayStr = `${nextDay.toISOString().split('T')[0]}T00:00:00Z`
      return `${columnCode} lt ${nextDayStr}`
    }
    return ''
  }

  const updateDateFilter = (stageId: string, fieldId: string, start: string | null, end: string | null) => {
    const field = fieldMap.get(fieldId)
    if (!field) return
    const otherFilters = state.draftStageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === fieldId))
    if (!start && !end) {
      dispatch({ type: 'SET_STAGE_FILTERS', payload: otherFilters })
      return
    }
    const filterString = buildDateFilterString(field.columnCode, start, end)
    const metadata = { start, end }
    if (filterString) {
      dispatch({ type: 'SET_STAGE_FILTERS', payload: [...otherFilters, { stageId, fieldConfigurationId: fieldId, filter: filterString, values: JSON.stringify(metadata) }] })
    } else {
      dispatch({ type: 'SET_STAGE_FILTERS', payload: otherFilters })
    }
  }

  const updateGlobalDateFilter = (fieldId: string, start: string | null, end: string | null) => {
    const field = fieldMap.get(fieldId)
    if (!field) return
    const existing = state.draftGlobalFilters.filter(f => f.fieldConfigurationId !== fieldId)
    if (!start && !end) {
      dispatch({ type: 'SET_GLOBAL_FILTERS', payload: existing })
      return
    }
    const filterBody = buildDateFilterString(field.columnCode, start, end)
    if (!filterBody) { dispatch({ type: 'SET_GLOBAL_FILTERS', payload: existing }); return }
    const filterString = `(Stage ne 'Discarded' and IsDeleted eq false) and ${filterBody}`
    const metadata = { start, end }
    dispatch({ type: 'SET_GLOBAL_FILTERS', payload: [...existing, { fieldConfigurationId: fieldId, filter: filterString, values: JSON.stringify(metadata) } as any] })
  }

  const updateGlobalSearchFilter = (fieldId: string, searchText: string) => {
    const field = fieldMap.get(fieldId)
    if (!field) return
    const existing = state.draftGlobalFilters.filter(f => f.fieldConfigurationId !== fieldId)
    if (!searchText.trim()) { dispatch({ type: 'SET_GLOBAL_FILTERS', payload: existing }); return }
    const filterBody = buildSearchableFilter(searchText, field.columnCode)
    const filterString = `(Stage ne 'Discarded' and IsDeleted eq false) and ${filterBody}`
    const metadata = { blank: false, search: searchText.trim() }
    dispatch({ type: 'SET_GLOBAL_FILTERS', payload: [...existing, { fieldConfigurationId: fieldId, filter: filterString, values: JSON.stringify(metadata) } as any] })
  }

  // ── Update functions (same filter string building as before) ──

  // Delegates to the shared canonical builder so the view designer emits exactly the
  // same OData the grid popovers / sidebar / drawer produce for the same selection —
  // this used to have its own copy whose blank branches were type-blind (`eq ''` +
  // quoted 'true' on Edm.Boolean fields → the whole search request 400'd).
  const buildFilterString = (field: any, values: string[], isGlobal: boolean): string => {
    const filterBody = buildFacetedValuesFilter(field, values)
    if (!filterBody) return ''
    return isGlobal ? `(Stage ne 'Discarded' and IsDeleted eq false) and ${filterBody}` : filterBody
  }

  const updateGlobalFilter = (fieldId: string, values: string[]) => {
    const field = fieldMap.get(fieldId)
    if (!field) return
    if (values.length === 0) {
      dispatch({ type: 'SET_GLOBAL_FILTERS', payload: state.draftGlobalFilters.filter(f => f.fieldConfigurationId !== fieldId) })
      return
    }
    const hasBlanks = values.includes('__BLANK__')
    const regularValues = values.filter(v => v !== '__BLANK__')
    const filterString = buildFilterString(field, values, true)
    const metadata = { isSelectAll: false, hasBlanks, regularValues }
    const existing = state.draftGlobalFilters.filter(f => f.fieldConfigurationId !== fieldId)
    if (filterString) dispatch({ type: 'SET_GLOBAL_FILTERS', payload: [...existing, { fieldConfigurationId: fieldId, filter: filterString, values: JSON.stringify(metadata) } as any] })
    else dispatch({ type: 'SET_GLOBAL_FILTERS', payload: existing })
  }

  const updateStageFilter = (stageId: string, fieldId: string, values: string[]) => {
    const field = fieldMap.get(fieldId)
    if (!field) return
    const otherFilters = state.draftStageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === fieldId))
    if (values.length === 0) { dispatch({ type: 'SET_STAGE_FILTERS', payload: otherFilters }); return }
    const hasBlanks = values.includes('__BLANK__')
    const regularValues = values.filter(v => v !== '__BLANK__')
    const filterString = buildFilterString(field, values, false)
    const metadata = { isSelectAll: false, hasBlanks, regularValues }
    if (filterString) dispatch({ type: 'SET_STAGE_FILTERS', payload: [...otherFilters, { stageId, fieldConfigurationId: fieldId, filter: filterString, values: JSON.stringify(metadata) }] })
    else dispatch({ type: 'SET_STAGE_FILTERS', payload: otherFilters })
  }

  const updateStageSearchFilter = (stageId: string, fieldId: string, searchText: string) => {
    const field = fieldMap.get(fieldId)
    if (!field) return
    const otherFilters = state.draftStageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === fieldId))
    if (!searchText.trim()) { dispatch({ type: 'SET_STAGE_FILTERS', payload: otherFilters }); return }
    const filterString = buildSearchableFilter(searchText, field.columnCode)
    const metadata = { blank: false, search: searchText.trim() }
    dispatch({ type: 'SET_STAGE_FILTERS', payload: [...otherFilters, { stageId, fieldConfigurationId: fieldId, filter: filterString, values: JSON.stringify(metadata) }] })
  }

  const removeGlobalFilter = (fieldId: string) => {
    dispatch({ type: 'SET_GLOBAL_FILTERS', payload: state.draftGlobalFilters.filter(f => f.fieldConfigurationId !== fieldId) })
  }

  const removeStageFilter = (stageId: string, fieldId: string) => {
    dispatch({ type: 'SET_STAGE_FILTERS', payload: state.draftStageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === fieldId)) })
  }

  // ── Revert helpers ──

  const originalGlobalFilters = state.originalConfig?.savedGlobalFilters || []
  const originalStageFilters = state.originalConfig?.savedStageFilters || []

  const isGlobalDirty = JSON.stringify(state.draftGlobalFilters) !== JSON.stringify(originalGlobalFilters)
  const isStageDirty = (stageId: string) => {
    const origForStage = originalStageFilters.filter((f: any) => f.stageId === stageId || f.StageId === stageId)
    const draftForStage = state.draftStageFilters.filter(f => f.stageId === stageId)
    return JSON.stringify(draftForStage) !== JSON.stringify(origForStage)
  }

  const revertGlobalFilters = () => dispatch({ type: 'SET_GLOBAL_FILTERS', payload: originalGlobalFilters })

  // Revert a single filter to its original value
  const revertSingleFilter = (fieldId: string, type: 'global' | 'stage', stageId?: string) => {
    if (type === 'global') {
      const origFilter = originalGlobalFilters.find((f: any) => f.fieldConfigurationId === fieldId)
      const otherDraft = state.draftGlobalFilters.filter(f => f.fieldConfigurationId !== fieldId)
      dispatch({ type: 'SET_GLOBAL_FILTERS', payload: origFilter ? [...otherDraft, origFilter] : otherDraft })
    } else if (stageId) {
      const origFilter = originalStageFilters.find((f: any) => (f.stageId === stageId || f.StageId === stageId) && f.fieldConfigurationId === fieldId)
      const otherDraft = state.draftStageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === fieldId))
      dispatch({ type: 'SET_STAGE_FILTERS', payload: origFilter ? [...otherDraft, origFilter] : otherDraft })
    }
  }

  // Check if a single filter has changed from original
  const isSingleFilterDirty = (fieldId: string, type: 'global' | 'stage', stageId?: string): boolean => {
    if (type === 'global') {
      const orig = originalGlobalFilters.find((f: any) => f.fieldConfigurationId === fieldId)
      const draft = state.draftGlobalFilters.find(f => f.fieldConfigurationId === fieldId)
      return JSON.stringify(orig) !== JSON.stringify(draft)
    }
    if (stageId) {
      const orig = originalStageFilters.find((f: any) => (f.stageId === stageId || f.StageId === stageId) && f.fieldConfigurationId === fieldId)
      const draft = state.draftStageFilters.find(f => f.stageId === stageId && f.fieldConfigurationId === fieldId)
      return JSON.stringify(orig) !== JSON.stringify(draft)
    }
    return false
  }
  const revertStageFilters = (stageId: string) => {
    const origForStage = originalStageFilters.filter((f: any) => f.stageId === stageId || f.StageId === stageId)
    const otherDraft = state.draftStageFilters.filter(f => f.stageId !== stageId)
    dispatch({ type: 'SET_STAGE_FILTERS', payload: [...otherDraft, ...origForStage] })
  }

  // ── Stage filter fields (expanded to include isSearchable) ──

  const getStageFilterFields = (stageId: string) => {
    const sc = stages.find((s: any) => (s.stageId || s.stageCode) === stageId)
    return (sc?.fieldConfigurations || []).filter((f: any) => f.isFacetable || f.isFilterable || f.isSearchable)
  }

  // Global filter fields
  const globalFilterFields = useMemo(() => {
    return globalFilterIds.map((fid: string) => fieldMap.get(fid)).filter(Boolean)
  }, [globalFilterIds, fieldMap])

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left sidebar */}
      <div className="w-52 border-r border-gray-200 bg-gradient-to-b from-[#F4F3FF] to-[#FAFAFF] flex-shrink-0 flex flex-col">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={16} className="text-[#0F214B]" />
            <h3 className="text-sm font-bold text-[#0F214B]">{t('atsSyncFilters' as any) || 'Filters'}</h3>
          </div>

          <div className="space-y-1.5">
            {/* Overview */}
            <button
              onClick={() => handleSetActiveView('overview')}
              className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeView === 'overview'
                  ? 'bg-white text-[#0F214B] shadow-md border border-blue-200 ring-1 ring-blue-100'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/60 border border-transparent'
              }`}
            >
              <Filter size={14} />
              Overview
              {(state.draftGlobalFilters.length + state.draftStageFilters.length) > 0 && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold">
                  {state.draftGlobalFilters.length + state.draftStageFilters.length}
                </span>
              )}
            </button>

            <div className="h-px bg-gray-200 my-2" />

            {/* Global */}
            <button
              onClick={() => handleSetActiveView('global')}
              className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeView === 'global'
                  ? 'bg-white text-[#0F214B] shadow-md border border-blue-200 ring-1 ring-blue-100'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/60 border border-transparent'
              }`}
            >
              <Globe size={14} />
              {t('vmGlobalFilters' as any) || 'Global'}
              {state.draftGlobalFilters.length > 0 && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold">
                  {state.draftGlobalFilters.length}
                </span>
              )}
            </button>

            {/* Stage */}
            <button
              onClick={() => handleSetActiveView('stage')}
              className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeView === 'stage'
                  ? 'bg-white text-[#0F214B] shadow-md border border-blue-200 ring-1 ring-blue-100'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/60 border border-transparent'
              }`}
            >
              <Layers size={14} />
              {t('vmStageFilters' as any) || 'Stage'}
              {state.draftStageFilters.length > 0 && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold">
                  {state.draftStageFilters.length}
                </span>
              )}
            </button>

            {/* Stage list when stage is active */}
            {activeView === 'stage' && (
              <div className="ml-4 space-y-1 mt-1">
                {stages.map((stage: any) => {
                  const stageId = stage.stageId || stage.stageCode
                  const title = getLocalizedField(stage, 'title') || stage.stageCode
                  const count = state.draftStageFilters.filter(f => f.stageId === stageId).length
                  const isActive = activeStageForFilters === stageId
                  return (
                    <button
                      key={stageId}
                      onClick={() => handleSetActiveStage(stageId)}
                      className={`w-full text-left px-3 py-1.5 rounded text-xs font-medium transition-all flex items-center justify-between ${
                        isActive ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-gray-500 hover:text-gray-700 hover:bg-white/60 border border-transparent'
                      }`}
                    >
                      <span className="truncate">{title}</span>
                      {count > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold">{count}</span>}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right: Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* ── Overview View — two-column: pills left, editor right ── */}
        {activeView === 'overview' && (
          <div className="flex flex-1 overflow-hidden">
            {/* Left: filter pills */}
            <div className="flex-1 p-6 overflow-auto">
              <h3 className="text-sm font-semibold text-[#0F214B] mb-1 flex items-center gap-2">
                <Filter size={16} />
                Active Filters Overview
              </h3>
              <p className="text-xs text-gray-400 mb-4">Click a filter to edit it. Click × to remove.</p>

              <div className="space-y-4">
                {/* Global filters — always shown (Item 6: revert always available when dirty) */}
                <div className="border border-gray-200 rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#0F214B]">
                      <Globe size={13} /> Global Filters
                    </div>
                    <div className="flex items-center gap-2">
                      {isGlobalDirty && (
                        <button onClick={revertGlobalFilters} className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 rounded-md border border-red-200 hover:bg-red-50 transition-colors">
                          <RotateCcw size={12} /> Revert
                        </button>
                      )}
                      {/* Item 2: Add Filter button for global */}
                      <div className="relative" ref={addFilterTarget?.type === 'global' && !addFilterTarget?.stageId ? addFilterRef : undefined}>
                        <button
                          onClick={() => { setAddFilterTarget(addFilterTarget?.type === 'global' && !addFilterTarget?.stageId ? null : { type: 'global' }); setAddFilterSearch('') }}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded-md border border-blue-200 hover:bg-blue-50 transition-colors"
                        >
                          <Plus size={12} /> {t('vmAddFilter' as any) || 'Add Filter'}
                        </button>
                        {addFilterTarget?.type === 'global' && !addFilterTarget?.stageId && (() => {
                          const activeIds = new Set(state.draftGlobalFilters.map(f => f.fieldConfigurationId))
                          const available = globalFilterFields.filter((f: any) => !activeIds.has(f.fieldConfigurationId))
                          const filtered = addFilterSearch
                            ? available.filter((f: any) => (getLocalizedField(f, 'columnName') || f.columnCode || '').toLowerCase().includes(addFilterSearch.toLowerCase()))
                            : available
                          return (
                            <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                              <div className="p-2 border-b border-gray-100">
                                <Input
                                  autoFocus
                                  placeholder={t('vmSearchFields' as any) || 'Search fields...'}
                                  value={addFilterSearch}
                                  onChange={(e) => setAddFilterSearch(e.target.value)}
                                  className="h-7 text-xs"
                                />
                              </div>
                              <div className="max-h-48 overflow-y-auto">
                                {filtered.length === 0 ? (
                                  <div className="px-3 py-3 text-xs text-gray-400 text-center">{t('vmNoMatchingFields' as any) || 'No fields match your search'}</div>
                                ) : filtered.map((field: any) => (
                                  <button
                                    key={field.fieldConfigurationId}
                                    onClick={() => {
                                      setSelectedFilter({ fieldId: field.fieldConfigurationId, type: 'global' })
                                      setAddFilterTarget(null)
                                      setAddFilterSearch('')
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 transition-colors"
                                  >
                                    {getLocalizedField(field, 'columnName') || field.columnCode}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  </div>
                  {state.draftGlobalFilters.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {state.draftGlobalFilters.map(f => {
                        const isActive = selectedFilter?.type === 'global' && selectedFilter?.fieldId === f.fieldConfigurationId
                        const preview = getFilterPreview(f)
                        return (
                          <button
                            key={f.fieldConfigurationId}
                            onClick={() => setSelectedFilter({ fieldId: f.fieldConfigurationId, type: 'global' })}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-all cursor-pointer ${
                              isActive
                                ? 'bg-blue-100 text-blue-800 border-blue-400 ring-1 ring-blue-300'
                                : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
                            }`}
                          >
                            <span className="font-semibold">{getFieldName(f.fieldConfigurationId)}:</span>
                            <span className="max-w-[150px] truncate">{preview.text}</span>
                            {preview.overflow > 0 && (
                              <span className="text-[10px] bg-blue-200/60 text-blue-700 rounded-full px-1.5 flex-shrink-0">+{preview.overflow}</span>
                            )}
                            <span onClick={(e) => { e.stopPropagation(); removeGlobalFilter(f.fieldConfigurationId); if (isActive) setSelectedFilter(null) }} className="hover:text-red-500 ml-0.5"><X size={11} /></span>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic py-1">No global filters configured</p>
                  )}
                </div>

                {/* Stage filters — show ALL stages (Item 3) */}
                {stages.map((stage: any) => {
                  const stageId = stage.stageId || stage.stageCode
                  const stageFilters = state.draftStageFilters.filter(f => f.stageId === stageId)
                  return (
                    <div key={stageId} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-xs font-semibold text-[#0F214B]">
                          <Layers size={13} /> {getStageName(stageId)}
                          {stageFilters.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full font-semibold">{stageFilters.length}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isStageDirty(stageId) && (
                            <button onClick={() => revertStageFilters(stageId)} className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 rounded-md border border-red-200 hover:bg-red-50 transition-colors">
                              <RotateCcw size={12} /> Revert
                            </button>
                          )}
                          {/* Item 2: Add Filter button for stage */}
                          <div className="relative" ref={addFilterTarget?.type === 'stage' && addFilterTarget?.stageId === stageId ? addFilterRef : undefined}>
                            <button
                              onClick={() => { setAddFilterTarget(addFilterTarget?.type === 'stage' && addFilterTarget?.stageId === stageId ? null : { type: 'stage', stageId }); setAddFilterSearch('') }}
                              className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium px-2 py-1 rounded-md border border-purple-200 hover:bg-purple-50 transition-colors"
                            >
                              <Plus size={12} /> {t('vmAddFilter' as any) || 'Add Filter'}
                            </button>
                            {addFilterTarget?.type === 'stage' && addFilterTarget?.stageId === stageId && (() => {
                              const stageFields = getStageFilterFields(stageId)
                              const activeIds = new Set(stageFilters.map(f => f.fieldConfigurationId))
                              const available = stageFields.filter((f: any) => !activeIds.has(f.fieldConfigurationId))
                              const filtered = addFilterSearch
                                ? available.filter((f: any) => (getLocalizedField(f, 'columnName') || f.columnCode || '').toLowerCase().includes(addFilterSearch.toLowerCase()))
                                : available
                              return (
                                <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                  <div className="p-2 border-b border-gray-100">
                                    <Input
                                      autoFocus
                                      placeholder={t('vmSearchFields' as any) || 'Search fields...'}
                                      value={addFilterSearch}
                                      onChange={(e) => setAddFilterSearch(e.target.value)}
                                      className="h-7 text-xs"
                                    />
                                  </div>
                                  <div className="max-h-48 overflow-y-auto">
                                    {filtered.length === 0 ? (
                                      <div className="px-3 py-3 text-xs text-gray-400 text-center">{t('vmNoMatchingFields' as any) || 'No fields match your search'}</div>
                                    ) : filtered.map((field: any) => (
                                      <button
                                        key={field.fieldConfigurationId}
                                        onClick={() => {
                                          setSelectedFilter({ fieldId: field.fieldConfigurationId, stageId, type: 'stage' })
                                          setAddFilterTarget(null)
                                          setAddFilterSearch('')
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-purple-50 transition-colors"
                                      >
                                        {getLocalizedField(field, 'columnName') || field.columnCode}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                      </div>
                      {stageFilters.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {stageFilters.map(f => {
                            const isActive = selectedFilter?.type === 'stage' && selectedFilter?.fieldId === f.fieldConfigurationId && selectedFilter?.stageId === stageId
                            const preview = getFilterPreview(f)
                            return (
                              <button
                                key={f.fieldConfigurationId}
                                onClick={() => setSelectedFilter({ fieldId: f.fieldConfigurationId, stageId, type: 'stage' })}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-all cursor-pointer ${
                                  isActive
                                    ? 'bg-purple-100 text-purple-800 border-purple-400 ring-1 ring-purple-300'
                                    : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 hover:border-purple-300'
                                }`}
                              >
                                <span className="font-semibold">{getFieldName(f.fieldConfigurationId)}:</span>
                                <span className="max-w-[150px] truncate">{preview.text}</span>
                                {preview.overflow > 0 && (
                                  <span className="text-[10px] bg-purple-200/60 text-purple-700 rounded-full px-1.5 flex-shrink-0">+{preview.overflow}</span>
                                )}
                                <span onClick={(e) => { e.stopPropagation(); removeStageFilter(stageId, f.fieldConfigurationId); if (isActive) setSelectedFilter(null) }} className="hover:text-red-500 ml-0.5"><X size={11} /></span>
                              </button>
                            )
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic py-1">{t('vmNoStageFilters' as any) || 'No stage filters configured'}</p>
                      )}

                      {/* Sort configuration pill */}
                      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                        {(() => {
                          const currentSort = state.draftSortByStage[stageId] || null
                          const stageFieldConfigs = (stages.find((s: any) => (s.stageId || s.stageCode) === stageId)?.fieldConfigurations || [])
                          const sortableFields = stageFieldConfigs.filter((f: any) => f.isSortable !== false)

                          if (currentSort) {
                            const sortField = fieldMap.get(currentSort.columnCode) || stageFieldConfigs.find((f: any) => f.columnCode === currentSort.columnCode || f.fieldConfigurationId === currentSort.columnCode)
                            const sortFieldName = sortField ? (getLocalizedField(sortField, 'columnName') || sortField.columnCode) : currentSort.columnCode
                            return (
                              <>
                                <span
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border bg-amber-50 text-amber-800 border-amber-300 cursor-pointer hover:bg-amber-100 transition-all"
                                  onClick={() => {
                                    // Toggle direction on click
                                    dispatch({ type: 'SET_STAGE_SORT', payload: { stageId, sort: { columnCode: currentSort.columnCode, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' } } })
                                  }}
                                >
                                  {currentSort.direction === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                                  <span className="font-semibold">{t('vmSortedBy' as any) || 'Sorted by'}:</span>
                                  <span>{sortFieldName}</span>
                                  <span className="text-[10px] text-amber-600">({currentSort.direction === 'asc' ? (t('vmAscending' as any) || 'A→Z') : (t('vmDescending' as any) || 'Z→A')})</span>
                                  <span
                                    onClick={(e) => { e.stopPropagation(); dispatch({ type: 'SET_STAGE_SORT', payload: { stageId, sort: null } }) }}
                                    className="hover:text-red-500 ml-0.5"
                                  >
                                    <X size={11} />
                                  </span>
                                </span>
                                {/* Change sort field button */}
                                <div className="relative" ref={sortDropdownStageId === stageId ? sortDropdownRef : undefined}>
                                  <button
                                    onClick={() => { setSortDropdownStageId(sortDropdownStageId === stageId ? null : stageId); setSortDropdownSearch('') }}
                                    className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium px-2 py-1 rounded-md border border-amber-200 hover:bg-amber-50 transition-colors"
                                  >
                                    <ChevronDown size={12} /> Change
                                  </button>
                                  {sortDropdownStageId === stageId && (
                                    <div className="absolute left-0 top-full mt-1 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                      <div className="p-2 border-b border-gray-100">
                                        <Input
                                          autoFocus
                                          placeholder={t('vmSearchFields' as any) || 'Search fields...'}
                                          value={sortDropdownSearch}
                                          onChange={(e) => setSortDropdownSearch(e.target.value)}
                                          className="h-7 text-xs"
                                        />
                                      </div>
                                      <div className="max-h-48 overflow-y-auto">
                                        {(() => {
                                          const filtered = sortDropdownSearch
                                            ? sortableFields.filter((f: any) => (getLocalizedField(f, 'columnName') || f.columnCode || '').toLowerCase().includes(sortDropdownSearch.toLowerCase()))
                                            : sortableFields
                                          if (filtered.length === 0) return <div className="px-3 py-3 text-xs text-gray-400 text-center">{t('vmNoMatchingFields' as any) || 'No fields match your search'}</div>
                                          return filtered.map((field: any) => {
                                            const colCode = field.columnCode
                                            const isCurrentSort = currentSort?.columnCode === colCode
                                            return (
                                              <button
                                                key={field.fieldConfigurationId || colCode}
                                                onClick={() => {
                                                  dispatch({ type: 'SET_STAGE_SORT', payload: { stageId, sort: { columnCode: colCode, direction: 'asc' } } })
                                                  setSortDropdownStageId(null)
                                                  setSortDropdownSearch('')
                                                }}
                                                className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between ${isCurrentSort ? 'bg-amber-50 text-amber-800 font-semibold' : 'hover:bg-amber-50'}`}
                                              >
                                                <span>{getLocalizedField(field, 'columnName') || field.columnCode}</span>
                                                {isCurrentSort && (currentSort?.direction === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                                              </button>
                                            )
                                          })
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </>
                            )
                          }

                          // No sort set — show "Set Sort" button with dropdown
                          return (
                            <div className="relative" ref={sortDropdownStageId === stageId ? sortDropdownRef : undefined}>
                              <button
                                onClick={() => { setSortDropdownStageId(sortDropdownStageId === stageId ? null : stageId); setSortDropdownSearch('') }}
                                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-amber-600 font-medium px-2 py-1 rounded-md border border-dashed border-gray-300 hover:border-amber-300 hover:bg-amber-50 transition-colors"
                              >
                                <ArrowUpDown size={12} /> {t('vmSelectSortField' as any) || 'Set sort order'}
                              </button>
                              {sortDropdownStageId === stageId && (
                                <div className="absolute left-0 top-full mt-1 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                  <div className="p-2 border-b border-gray-100">
                                    <Input
                                      autoFocus
                                      placeholder={t('vmSearchFields' as any) || 'Search fields...'}
                                      value={sortDropdownSearch}
                                      onChange={(e) => setSortDropdownSearch(e.target.value)}
                                      className="h-7 text-xs"
                                    />
                                  </div>
                                  <div className="max-h-48 overflow-y-auto">
                                    {(() => {
                                      const filtered = sortDropdownSearch
                                        ? sortableFields.filter((f: any) => (getLocalizedField(f, 'columnName') || f.columnCode || '').toLowerCase().includes(sortDropdownSearch.toLowerCase()))
                                        : sortableFields
                                      if (filtered.length === 0) return <div className="px-3 py-3 text-xs text-gray-400 text-center">{t('vmNoMatchingFields' as any) || 'No fields match your search'}</div>
                                      return filtered.map((field: any) => {
                                        const colCode = field.columnCode
                                        return (
                                          <button
                                            key={field.fieldConfigurationId || colCode}
                                            onClick={() => {
                                              dispatch({ type: 'SET_STAGE_SORT', payload: { stageId, sort: { columnCode: colCode, direction: 'asc' } } })
                                              setSortDropdownStageId(null)
                                              setSortDropdownSearch('')
                                            }}
                                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-amber-50 transition-colors"
                                          >
                                            {getLocalizedField(field, 'columnName') || field.columnCode}
                                          </button>
                                        )
                                      })
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Right: Filter detail editor panel */}
            {selectedFilter && (() => {
              const field = fieldMap.get(selectedFilter.fieldId)
              if (!field) return null
              const name = getLocalizedField(field, 'columnName') || field.columnCode
              const isGlobal = selectedFilter.type === 'global'
              const stageId = selectedFilter.stageId || ''

              return (
                <div className="w-96 border-l border-gray-200 bg-gradient-to-b from-[#F8F8FF] to-white flex flex-col flex-shrink-0 overflow-y-auto">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-[#F0EEFF]">
                    <div>
                      <h3 className="text-sm font-bold text-[#0F214B]">{name}</h3>
                      <p className="text-[10px] text-gray-500">{isGlobal ? 'Global Filter' : `Stage: ${getStageName(stageId)}`}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {isSingleFilterDirty(selectedFilter.fieldId, selectedFilter.type, stageId) && (
                        <button
                          onClick={() => revertSingleFilter(selectedFilter.fieldId, selectedFilter.type, stageId)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 rounded-md border border-red-200 hover:bg-red-50 transition-colors"
                        >
                          <RotateCcw size={11} /> Revert
                        </button>
                      )}
                      <button onClick={() => setSelectedFilter(null)} className="p-1 rounded hover:bg-gray-100">
                        <X size={16} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4 flex-1">
                    {isDateField(field) ? (
                      <DateRangeFilterInput
                        title={name}
                        start={isGlobal ? getGlobalDateFilterValues(selectedFilter.fieldId).start : getDateFilterValues(stageId, selectedFilter.fieldId).start}
                        end={isGlobal ? getGlobalDateFilterValues(selectedFilter.fieldId).end : getDateFilterValues(stageId, selectedFilter.fieldId).end}
                        onChange={(s, e) => {
                          if (isGlobal) updateGlobalDateFilter(selectedFilter.fieldId, s, e)
                          else updateDateFilter(stageId, selectedFilter.fieldId, s, e)
                        }}
                      />
                    ) : isSearchableOnly(field) ? (
                      <SearchFilterInput
                        title={name}
                        value={isGlobal ? getGlobalFilterSearch(selectedFilter.fieldId) : getStageFilterSearch(stageId, selectedFilter.fieldId)}
                        onChange={(text) => {
                          if (isGlobal) updateGlobalSearchFilter(selectedFilter.fieldId, text)
                          else updateStageSearchFilter(stageId, selectedFilter.fieldId, text)
                        }}
                        columnCode={field.columnCode}
                        fetchSuggestions={fetchSearchSuggestions}
                      />
                    ) : (
                      <FilterValueSelector
                        title={name}
                        options={getFacetOptions(selectedFilter.fieldId, isGlobal)}
                        selectedValues={isGlobal
                          ? getGlobalFilterSelections(selectedFilter.fieldId)
                          : getStageFilterSelections(stageId, selectedFilter.fieldId)
                        }
                        onChange={(vals) => {
                          if (isGlobal) updateGlobalFilter(selectedFilter.fieldId, vals)
                          else updateStageFilter(stageId, selectedFilter.fieldId, vals)
                        }}
                      />
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
        )}

        {/* ── Global Filters View ── */}
        {activeView === 'global' && (
          <div className="p-6 overflow-auto flex-1">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[#0F214B] flex items-center gap-2">
                  <Globe size={16} /> {t('vmGlobalFilters' as any) || 'Global Filters'}
                </h3>
                <p className="text-xs text-gray-400 mt-1">Select filter values for fields that apply across all stages.</p>
              </div>
              {isGlobalDirty && (
                <Button variant="elliptical-outline" size="sm" onClick={revertGlobalFilters} className="text-red-500 border-red-200 hover:bg-red-50 text-xs">
                  <RotateCcw size={12} className="mr-1" /> Revert Global
                </Button>
              )}
            </div>
            {globalFilterFields.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-gray-400">
                <Filter size={32} className="mb-3 opacity-30" />
                <p className="text-sm">No global filter fields configured.</p>
              </div>
            ) : (
              <div className="space-y-3 max-w-2xl">
                {/* Item 1: Sticky field search bar */}
                <div className="sticky top-0 z-10 bg-white pb-2 -mt-2 pt-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder={t('vmSearchFields' as any) || 'Search fields...'}
                      value={fieldSearchQuery}
                      onChange={(e) => setFieldSearchQuery(e.target.value)}
                      className="h-8 text-xs pl-8"
                    />
                    {fieldSearchQuery && (
                      <button onClick={() => setFieldSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
                {(() => {
                  const filtered = fieldSearchQuery
                    ? globalFilterFields.filter((f: any) => (getLocalizedField(f, 'columnName') || f.columnCode || '').toLowerCase().includes(fieldSearchQuery.toLowerCase()))
                    : globalFilterFields
                  if (filtered.length === 0) return (
                    <div className="flex flex-col items-center py-8 text-gray-400">
                      <Search size={24} className="mb-2 opacity-30" />
                      <p className="text-xs">{t('vmNoMatchingFields' as any) || 'No fields match your search'}</p>
                    </div>
                  )
                  return filtered.map((field: any) => {
                    const fieldId = field.fieldConfigurationId
                    const name = getLocalizedField(field, 'columnName') || field.columnCode
                    const options = getFacetOptions(fieldId, true)
                    const selectedValues = getGlobalFilterSelections(fieldId)
                    return (
                      <FilterValueSelector key={fieldId} title={name} options={options} selectedValues={selectedValues} onChange={(vals) => updateGlobalFilter(fieldId, vals)} />
                    )
                  })
                })()}
              </div>
            )}
          </div>
        )}

        {/* ── Stage Filters View ── */}
        {activeView === 'stage' && (
          <div className="p-6 overflow-auto flex-1">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[#0F214B] flex items-center gap-2">
                  <Layers size={16} /> {t('vmStageFilters' as any) || 'Stage Filters'}
                  <span className="font-normal text-gray-400">— {getStageName(activeStageForFilters)}</span>
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  Configure filters for the selected stage. For text-searchable fields, type and press Enter or select a suggestion.
                </p>
              </div>
              {isStageDirty(activeStageForFilters) && (
                <Button variant="elliptical-outline" size="sm" onClick={() => revertStageFilters(activeStageForFilters)} className="text-red-500 border-red-200 hover:bg-red-50 text-xs">
                  <RotateCcw size={12} className="mr-1" /> Revert Stage
                </Button>
              )}
            </div>
            {(() => {
              const stageFields = getStageFilterFields(activeStageForFilters)
              if (stageFields.length === 0) return (
                <div className="flex flex-col items-center py-12 text-gray-400">
                  <Filter size={32} className="mb-3 opacity-30" />
                  <p className="text-sm">No filterable fields on this stage.</p>
                </div>
              )
              const filteredStageFields = fieldSearchQuery
                ? stageFields.filter((f: any) => (getLocalizedField(f, 'columnName') || f.columnCode || '').toLowerCase().includes(fieldSearchQuery.toLowerCase()))
                : stageFields
              return (
                <div className="space-y-3 max-w-2xl">
                  {/* Sort order selector */}
                  {(() => {
                    const currentSort = state.draftSortByStage[activeStageForFilters] || null
                    const stageFieldConfigs = (stages.find((s: any) => (s.stageId || s.stageCode) === activeStageForFilters)?.fieldConfigurations || [])
                    const sortableFields = stageFieldConfigs.filter((f: any) => f.isSortable !== false)
                    const sortField = currentSort ? (fieldMap.get(currentSort.columnCode) || stageFieldConfigs.find((f: any) => f.columnCode === currentSort.columnCode || f.fieldConfigurationId === currentSort.columnCode)) : null
                    const sortFieldName = sortField ? (getLocalizedField(sortField, 'columnName') || sortField.columnCode) : currentSort?.columnCode || ''

                    return (
                      <div className="border border-amber-200 rounded-lg p-3 bg-amber-50/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
                            <ArrowUpDown size={13} />
                            {t('vmSortedBy' as any) || 'Sort Order'}
                          </div>
                          {currentSort && (
                            <button
                              onClick={() => dispatch({ type: 'SET_STAGE_SORT', payload: { stageId: activeStageForFilters, sort: null } })}
                              className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-0.5 rounded border border-red-200 hover:bg-red-50 transition-colors"
                            >
                              {t('vmNoSort' as any) || 'Clear Sort'}
                            </button>
                          )}
                        </div>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {currentSort ? (
                            <>
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                                {currentSort.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                {sortFieldName}
                              </span>
                              <button
                                onClick={() => dispatch({ type: 'SET_STAGE_SORT', payload: { stageId: activeStageForFilters, sort: { columnCode: currentSort.columnCode, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' } } })}
                                className="text-xs text-amber-600 hover:text-amber-700 font-medium px-2 py-1.5 rounded border border-amber-200 hover:bg-amber-100 transition-colors"
                              >
                                {currentSort.direction === 'asc' ? (t('vmDescending' as any) || 'Switch to Z→A') : (t('vmAscending' as any) || 'Switch to A→Z')}
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400 italic">{t('vmNoSort' as any) || 'No sort configured'}</span>
                          )}
                          <div className="relative" ref={sortDropdownStageId === activeStageForFilters ? sortDropdownRef : undefined}>
                            <button
                              onClick={() => { setSortDropdownStageId(sortDropdownStageId === activeStageForFilters ? null : activeStageForFilters); setSortDropdownSearch('') }}
                              className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium px-2 py-1.5 rounded border border-amber-200 hover:bg-amber-100 transition-colors"
                            >
                              <ChevronDown size={12} /> {currentSort ? 'Change Field' : (t('vmSelectSortField' as any) || 'Select Field')}
                            </button>
                            {sortDropdownStageId === activeStageForFilters && (
                              <div className="absolute left-0 top-full mt-1 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                <div className="p-2 border-b border-gray-100">
                                  <Input
                                    autoFocus
                                    placeholder={t('vmSearchFields' as any) || 'Search fields...'}
                                    value={sortDropdownSearch}
                                    onChange={(e) => setSortDropdownSearch(e.target.value)}
                                    className="h-7 text-xs"
                                  />
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {(() => {
                                    const filtered = sortDropdownSearch
                                      ? sortableFields.filter((f: any) => (getLocalizedField(f, 'columnName') || f.columnCode || '').toLowerCase().includes(sortDropdownSearch.toLowerCase()))
                                      : sortableFields
                                    if (filtered.length === 0) return <div className="px-3 py-3 text-xs text-gray-400 text-center">{t('vmNoMatchingFields' as any) || 'No fields match your search'}</div>
                                    return filtered.map((field: any) => {
                                      const colCode = field.columnCode
                                      const isCurrentSort = currentSort?.columnCode === colCode
                                      return (
                                        <button
                                          key={field.fieldConfigurationId || colCode}
                                          onClick={() => {
                                            dispatch({ type: 'SET_STAGE_SORT', payload: { stageId: activeStageForFilters, sort: { columnCode: colCode, direction: currentSort?.direction || 'asc' } } })
                                            setSortDropdownStageId(null)
                                            setSortDropdownSearch('')
                                          }}
                                          className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between ${isCurrentSort ? 'bg-amber-50 text-amber-800 font-semibold' : 'hover:bg-amber-50'}`}
                                        >
                                          <span>{getLocalizedField(field, 'columnName') || field.columnCode}</span>
                                          {isCurrentSort && (currentSort?.direction === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                                        </button>
                                      )
                                    })
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })()}

                  {/* Item 1: Sticky field search bar */}
                  <div className="sticky top-0 z-10 bg-white pb-2 -mt-2 pt-2">
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder={t('vmSearchFields' as any) || 'Search fields...'}
                        value={fieldSearchQuery}
                        onChange={(e) => setFieldSearchQuery(e.target.value)}
                        className="h-8 text-xs pl-8"
                      />
                      {fieldSearchQuery && (
                        <button onClick={() => setFieldSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  {filteredStageFields.length === 0 ? (
                    <div className="flex flex-col items-center py-8 text-gray-400">
                      <Search size={24} className="mb-2 opacity-30" />
                      <p className="text-xs">{t('vmNoMatchingFields' as any) || 'No fields match your search'}</p>
                    </div>
                  ) : filteredStageFields.map((field: any) => {
                    const fieldId = field.fieldConfigurationId
                    const name = getLocalizedField(field, 'columnName') || field.columnCode
                    if (isDateField(field)) {
                      const { start, end } = getDateFilterValues(activeStageForFilters, fieldId)
                      return (
                        <DateRangeFilterInput
                          key={fieldId}
                          title={name}
                          start={start}
                          end={end}
                          onChange={(s, e) => updateDateFilter(activeStageForFilters, fieldId, s, e)}
                        />
                      )
                    }
                    if (isSearchableOnly(field)) {
                      const searchValue = getStageFilterSearch(activeStageForFilters, fieldId)
                      return (
                        <SearchFilterInput
                          key={fieldId}
                          title={name}
                          value={searchValue}
                          onChange={(text) => updateStageSearchFilter(activeStageForFilters, fieldId, text)}
                          columnCode={field.columnCode}
                          fetchSuggestions={fetchSearchSuggestions}
                        />
                      )
                    }
                    const options = getFacetOptions(fieldId)
                    const selectedValues = getStageFilterSelections(activeStageForFilters, fieldId)
                    return (
                      <FilterValueSelector key={fieldId} title={name} options={options} selectedValues={selectedValues} onChange={(vals) => updateStageFilter(activeStageForFilters, fieldId, vals)} />
                    )
                  })}
                </div>
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
}
